import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { PaymentService } from '../services/payment/payment.service';
import { LoggingService } from '../services/core/logging.service';
import { OrderRequest } from './orders/order-request.model';
import { PaymentResource } from './orders/payment-templates.token';

/** Base class for payment components, this will take care of loading scripts and unloading them once destroyed manually.
 *  You need to extend this class when creating a new payment provider component template.
 * */
@Directive()
export abstract class BasePaymentComponent implements OnInit, OnDestroy {
  @Input() orderId: string = '';
  @Input() amount: number = 0;
  @Input() resources: PaymentResource[];
  @Input() paymentRequest: OrderRequest | null = null;
  abstract init(): any;
  abstract submit(): void;
  protected context: any;
  protected paymentService = inject(PaymentService);
  private logService = inject(LoggingService);
  private loadedScripts: HTMLScriptElement[] = [];
  isLoaded: boolean = false;
  protected properties: Record<string, any> = {};

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    // A payment request only exists for flows that create an order. Adding a card token or topping
    // up has no order, so its absence is normal and must NOT stop the component initialising —
    // bailing here left the provider's fields unmounted and the form permanently dead.
    if (!this.paymentRequest) {
      this.logService.debug('No payment request provided to the payment component; continuing without order urls');
    }

    this.context = this.buildContext(this.paymentRequest, this.resources);
    this.properties = this.context.properties;

    this.loadScripts(this.context.scripts).then((scripts) => {
      this.loadedScripts = scripts;
      this.isLoaded = true;
      this.init();
    });
  }

  ngOnDestroy(): void {
    // Should be manually destroyed from the payment service by calling the method destroyPaymentComponent()
    this.unloadScripts();
  }

  /* ================================
   * Public API
   * ================================ */

  loadScripts(scripts: string[]): Promise<any> {
    const scriptPromises = scripts.map((script) => this.paymentService.loadScript(script));
    return Promise.all(scriptPromises);
  }

  unloadScripts(): void {
    this.loadedScripts.forEach((script: any) => {
      if (script && script.el.parentNode) {
        script.el.parentNode.removeChild(script.el);
      }
    });
  }

  /* ================================
   * Private Helpers
   * ================================ */

  protected buildContext(paymentRequest: OrderRequest | null, resources: PaymentResource[]) {
    // Now that a missing paymentRequest no longer short-circuits ngOnInit, a provider that returns
    // no Resources at all reaches this point too; treat it as an empty list rather than throwing.
    const safeResources = resources ?? [];
    return {
      properties: this.getProperties(safeResources),
      scripts: safeResources.filter((r) => r.ResourceContentType === 'javascript').map((r) => r.Url),
      callbackUrl: paymentRequest?.callbackUrl || '',
      successUrl: paymentRequest?.successUrl || '',
      failureUrl: paymentRequest?.failureUrl || '',
    };
  }

  protected getProperties(resources: PaymentResource[]): Record<string, any> {
    return resources
      .filter((r) => this.isNotEmpty(r.Properties))
      .map((r) => r.Properties)
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
  }

  protected isNotEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length > 0;
  }
}
